import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { SelectOption } from '@/features/editMode/types/selectOption';
import type { NotificationsSliceState, SetNotificationsPayload } from '@/features/notifications/store/types';
import { NotificationStatusLookup } from '@/features/notifications/enums/NotificationStatuses';
import { filterAndSortNotificationsByStatus } from '@/features/notifications/utils/filterNotificationsByStatus';
import { ServerSideEvents } from '@/services/ServerEventService/events';
import { findOptionByLookup } from '@/utils/selectOptionUtils';

const initialState: NotificationsSliceState = {
  isOpen: false,
  notifications: [],
  notificationStatusOptions: [],
};

const notificationsSlice = createSlice({
  name: 'notificationsSlice',
  initialState,
  reducers: {
    setNotificationsOptions: (state, { payload }: PayloadAction<SelectOption[]>) => {
      state.notificationStatusOptions = payload;

      const unreadStatus = findOptionByLookup(state.notificationStatusOptions, NotificationStatusLookup.Unread);
      const readStatus = findOptionByLookup(state.notificationStatusOptions, NotificationStatusLookup.Read);
      const unreadNotifications = filterAndSortNotificationsByStatus(state.notifications, unreadStatus);
      const readNotifications = filterAndSortNotificationsByStatus(state.notifications, readStatus);

      state.notifications = [...unreadNotifications, ...readNotifications];
    },
    setNotifications: (state, { payload: { notifications, type } }: PayloadAction<SetNotificationsPayload>) => {
      switch (type) {
        case ServerSideEvents.CurrentNotifications.name: {
          state.notifications = notifications;
          return;
        }
        case ServerSideEvents.NewNotifications.name: {
          state.notifications = [...notifications, ...state.notifications];
          return;
        }
        case ServerSideEvents.ChangedNotifications.name: {
          const existingNotificationsMap = new Map(
            state.notifications.map((notification) => [notification.id, notification]),
          );

          notifications.forEach((notification) => {
            const existingNotification = existingNotificationsMap.get(notification.id);
            const isChanged =
              existingNotification && existingNotification.notificationStatusId !== notification.notificationStatusId;
            if (isChanged) {
              existingNotificationsMap.set(notification.id, {
                ...existingNotification,
                notificationStatusId: notification.notificationStatusId,
              });
            }
          });

          const updatedNotifications = Array.from(existingNotificationsMap.values());

          if (state.notificationStatusOptions.length) {
            const unreadStatus = findOptionByLookup(state.notificationStatusOptions, NotificationStatusLookup.Unread);
            const readStatus = findOptionByLookup(state.notificationStatusOptions, NotificationStatusLookup.Read);
            const unreadNotifications = filterAndSortNotificationsByStatus(updatedNotifications, unreadStatus);
            const readNotifications = filterAndSortNotificationsByStatus(updatedNotifications, readStatus);

            state.notifications = [...unreadNotifications, ...readNotifications];
          } else {
            state.notifications = [...updatedNotifications];
          }
        }
      }
    },
    setNotificationsOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.isOpen = payload;
    },
  },
});

export const { setNotifications, setNotificationsOpen, setNotificationsOptions } = notificationsSlice.actions;

export const notificationsSliceReducerPath = notificationsSlice.reducerPath;

export const notificationsSliceReducer = notificationsSlice.reducer;
